import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  TextField,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  InputAdornment,
  Stack,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Tune as TuneIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAdminInternalShipments } from '../../hooks/admin/useAdminInternalShipments';
import { InternalShipmentStatus } from '../../services/internalShipmentService';
import {
  INK,
  FONTS,
  ghostBtnSx,
  ledgerInputSx,
  ledgerSelectSx,
} from '../../styles/ledger';
import {
  LedgerPage,
  PageHeader,
  KPIStrip,
  KPI,
  FilterBar,
  LedgerCard,
  ActionIcon,
  StatusDot,
  EmptyState,
  ledgerTableHeadSx,
  ledgerRowSx,
} from '../../components/admin/LedgerPrimitives';

const STATUS_OPTIONS: Array<{ value: InternalShipmentStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All statuses' },
  { value: 'pending_review', label: 'Pending review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'in_transit', label: 'In transit' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];

const statusTone = (s: InternalShipmentStatus | string): 'active' | 'inactive' | 'warning' | 'info' | 'neutral' => {
  if (s === 'delivered') return 'active';
  if (s === 'cancelled' || s === 'rejected') return 'inactive';
  if (s === 'pending_review') return 'warning';
  if (s === 'approved' || s === 'in_transit') return 'info';
  return 'neutral';
};

const AdminInternalShipmentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<InternalShipmentStatus | 'all'>('all');

  const filters = useMemo(() => ({ search: search || undefined, status, per_page: 50 }), [search, status]);
  const { shipments, stats, isLoading, shipmentsQuery } = useAdminInternalShipments(filters);

  return (
    <LedgerPage eyebrowFooter="Almaship · Internal shipments">
      <PageHeader
        eyebrow="Admin · Logistics"
        title={
          <>
            Internal
            <Box component="span" sx={{ fontStyle: 'italic', color: INK.accent }}>
              {' '}shipments.
            </Box>
          </>
        }
        description="Review, track, and complete internal shipment requests from start to delivery."
        actions={
          <Button
            onClick={() => navigate('/admin/internal-shipment-rates')}
            startIcon={<TuneIcon sx={{ fontSize: 16 }} />}
            sx={ghostBtnSx}
          >
            Manage rates
          </Button>
        }
      />

      <KPIStrip cols={4}>
        <KPI label="Pending review" value={stats?.pendingReview ?? 0} sub="awaiting admin" accent />
        <KPI label="Approved" value={stats?.approved ?? 0} sub="ready to ship" />
        <KPI label="In transit" value={stats?.inTransit ?? 0} sub="on the move" />
        <KPI label="Delivered" value={stats?.delivered ?? 0} sub="completed" />
      </KPIStrip>

      <FilterBar>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search shipment number, tracking, item…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 16, color: INK.subtle }} />
                </InputAdornment>
              ),
            }}
            sx={ledgerInputSx}
          />
        </Box>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel sx={{ fontFamily: FONTS.sans, fontSize: 13, color: INK.muted, '&.Mui-focused': { color: INK.accent } }}>
            Status
          </InputLabel>
          <Select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as InternalShipmentStatus | 'all')}
            sx={ledgerSelectSx}
          >
            {STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value} sx={{ fontFamily: FONTS.sans, fontSize: 13 }}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </FilterBar>

      <LedgerCard padded={false}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={28} sx={{ color: INK.accent }} />
          </Box>
        ) : shipmentsQuery.error ? (
          <Box sx={{ p: 4 }}>
            <Alert severity="error" sx={{ borderRadius: 0, border: `1px solid ${INK.inactive}`, fontFamily: FONTS.sans }}>
              Failed to load internal shipments.
            </Alert>
          </Box>
        ) : (
          <TableContainer>
            <Table sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow sx={ledgerTableHeadSx}>
                  <TableCell>Shipment</TableCell>
                  <TableCell>User</TableCell>
                  <TableCell>Recipient</TableCell>
                  <TableCell>Tracking</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right" sx={{ width: 80 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {shipments.length > 0 ? (
                  shipments.map((shipment) => (
                    <TableRow key={shipment.id} sx={ledgerRowSx}>
                      <TableCell>
                        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 13, color: INK.text, fontWeight: 600 }}>
                          {shipment.shipmentNumber}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: INK.muted, mt: 0.25 }}>
                          {shipment.itemDescription}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontSize: 13, color: INK.text, fontWeight: 500, letterSpacing: '-0.005em' }}>
                          {shipment.user?.fullName || 'N/A'}
                        </Typography>
                        <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.subtle }}>
                          {shipment.user?.email || 'No email'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ fontSize: 13, color: INK.text }}>{shipment.recipient.contactName}</TableCell>
                      <TableCell>
                        {shipment.trackingNumber ? (
                          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 12, color: INK.text }}>
                            {shipment.trackingNumber}
                          </Typography>
                        ) : (
                          <Typography sx={{ fontFamily: FONTS.mono, fontSize: 11, color: INK.faint, fontStyle: 'italic' }}>
                            pending
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ fontFamily: FONTS.mono, fontSize: 12, color: INK.muted }}>
                        {shipment.createdAt ? format(new Date(shipment.createdAt), 'yyyy-MM-dd') : '—'}
                      </TableCell>
                      <TableCell>
                        <StatusDot label={shipment.statusLabel} tone={statusTone(shipment.status)} />
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" justifyContent="flex-end">
                          <ActionIcon title="View" onClick={() => navigate(`/admin/internal-shipments/${shipment.id}`)}>
                            <VisibilityIcon sx={{ fontSize: 15 }} />
                          </ActionIcon>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ borderBottom: 'none' }}>
                      <EmptyState
                        icon={<SearchIcon sx={{ fontSize: 22 }} />}
                        title="No shipments on record."
                        description="Adjust filters or wait for the next request."
                      />
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </LedgerCard>
    </LedgerPage>
  );
};

export default AdminInternalShipmentsPage;
